import { SelectField } from "~/components/forms";
import { getCountriesWithPhoneCodes } from "~/utils/countries/index";
import type { ListOfErrors } from '~/components/forms';
import { SelectProps } from "./ui/select";

interface PhoneCountrySelectProps {
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>;
  selectProps: SelectProps;
  errors?: ListOfErrors;
  language?: 'en' | 'es';
}

export function getFullPhoneCountryCode(dialCode: string | null | undefined): string {
  if (!dialCode) return "+34-ES"; // Default to Spain
  
  // If the dialCode already includes the country code (format: +xx-XX), return it as is
  if (dialCode.includes("-")) return dialCode;
  
  // Find the country that matches this dial code
  const phoneCountryCodes = getCountriesWithPhoneCodes();
  const country = phoneCountryCodes.find(c => c.dialCode === dialCode);
  
  // If found, return in the format expected by PhoneCountrySelect
  if (country) return `${dialCode}-${country.code}`;
  
  // Fallback to Spain if no match found
  return "+34-ES";
}

export function PhoneCountrySelect({
  labelProps,
  selectProps,
  errors,
  language = 'es'
}: PhoneCountrySelectProps) {
  const countries = getCountriesWithPhoneCodes(language);

  // Process the value to ensure it's in the correct format
  const processedSelectProps = {
    ...selectProps,
    value: selectProps.value ? getFullPhoneCountryCode(selectProps.value as string) : undefined,
    defaultValue: selectProps.defaultValue ? getFullPhoneCountryCode(selectProps.defaultValue as string) : "+34-ES"
  };

  const countryOptions = countries.map(country => ({
    value: `${country.dialCode}-${country.code}`,
    label: `(${country.dialCode}) ${country.name.substring(0, 10)}`
  }));

  return (
    <SelectField
      labelProps={labelProps}
      selectProps={processedSelectProps}
      options={countryOptions}
      errors={errors}
    />
  );
}
